/**
 * Login — docs/08-frontend-and-ux.md §3.1.
 *
 * The only screen a visitor ever sees, and it reveals nothing about the family:
 * no names, no counts, and one generic error for both unknown email and wrong
 * password (account enumeration is a stated threat, docs/09 §1.3).
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/client.js';
import { sessionQueryKey, useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import styles from '../../ui/components.module.css';

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, isResolved } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const next = params.get('next') ?? '/';

  const login = useMutation({
    mutationFn: () => authApi.login(email, password),
    onSuccess: (data) => {
      queryClient.setQueryData(sessionQueryKey, data.user);
      navigate(next, { replace: true });
    },
  });

  if (isResolved && user) return <Navigate to={next} replace />;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <main className={styles.centeredPage}>
      <div className={styles.centeredPanel}>
        <div className={styles.brand}>
          <h1 className={styles.brandName}>{t('app.name')}</h1>
          <p className={styles.brandTagline}>{t('app.tagline')}</p>
        </div>

        <form className={styles.card} onSubmit={onSubmit} noValidate>
          <h2>{t('login.title')}</h2>

          {login.isError && (
            <p className={styles.problem} role="alert">
              {problemMessage(login.error, t)}
            </p>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              {t('login.email')}
            </label>
            <input
              id="email"
              className={styles.input}
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="password">
              {t('login.password')}
            </label>
            <input
              id="password"
              className={styles.input}
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className={styles.muted}
              style={{
                background: 'none',
                border: 0,
                cursor: 'pointer',
                textAlign: 'start',
                padding: 0,
              }}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? t('login.hidePassword') : t('login.showPassword')}
            </button>
          </div>

          <button className={styles.button} type="submit" disabled={login.isPending}>
            {login.isPending ? t('login.submitting') : t('login.submit')}
          </button>

          <p className={styles.muted} style={{ marginBlockStart: 'var(--space-4)' }}>
            {t('login.inviteOnly')}
          </p>
        </form>
      </div>
    </main>
  );
}
